import { Alert, Button, Modal, Spin, Table } from 'antd'
import React from 'react'
import {
  SPARTACUS_LAST_SEEN_COLUMN_TOOLTIP,
  SPARTACUS_LAST_VENUE_ACTIVITY_TOOLTIP,
} from 'src/constants/spartacusTooltips'
import { SPARTACUS_LAST_SEEN_NOT_TRACKED_LABEL } from 'src/constants/spartacusFilters'
import { getSpartacusLastSeenTooltip } from 'src/utils/spartacusDateRange'
import {
  formatSpartacusLastSeenLabel,
  formatSpartacusPhoneLabel,
  formatSpartacusVenueActivityLabel,
  getSpartacusPhoneHref,
  hasUntrackedLastSeenUsers,
} from 'src/utils/spartacusMappers'
import { ColumnHeaderWithTooltip } from 'src/utils/venueProductFieldTooltips'
import styled from 'styled-components/macro'
import SpartacusPhoneCell from './SpartacusPhoneCell'
import { useSpartacusMobile } from './useSpartacusMobile'

const USER_COLUMNS = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    width: 160,
  },
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'email',
    width: 220,
  },
  {
    title: 'Role',
    dataIndex: 'role',
    key: 'role',
    width: 140,
  },
  {
    title: (
      <ColumnHeaderWithTooltip
        label='Last seen'
        tooltip={SPARTACUS_LAST_SEEN_COLUMN_TOOLTIP}
      />
    ),
    dataIndex: 'lastSeenLabel',
    key: 'lastSeenLabel',
    width: 150,
    render: (value, record) => (
      <LastSeenValue
        $untracked={value === SPARTACUS_LAST_SEEN_NOT_TRACKED_LABEL}
      >
        {formatSpartacusLastSeenLabel(record.lastSeenLabel)}
      </LastSeenValue>
    ),
  },
  {
    title: (
      <ColumnHeaderWithTooltip
        label='Last venue activity'
        tooltip={SPARTACUS_LAST_VENUE_ACTIVITY_TOOLTIP}
      />
    ),
    dataIndex: 'lastVenueActivityLabel',
    key: 'lastVenueActivityLabel',
    width: 170,
    render: (value, record) =>
      formatSpartacusVenueActivityLabel(record.lastVenueActivityLabel),
  },
]

function SpartacusVenueUsersModal({
  visible,
  venueName,
  venuePhone,
  usersData,
  loading,
  error,
  copying,
  onClose,
  onCopyEmails,
}) {
  const showLegacyMessage =
    usersData?.users && hasUntrackedLastSeenUsers(usersData.users)
  const phoneLabel = formatSpartacusPhoneLabel(venuePhone)
  const phoneHref = getSpartacusPhoneHref(venuePhone)
  const isMobile = useSpartacusMobile()

  return (
    <StyledModal
      visible={visible}
      title={venueName ? `Users — ${venueName}` : 'Venue users'}
      width={isMobile ? '100%' : 900}
      onCancel={onClose}
      footer={[
        <Button key='close' onClick={onClose} block={isMobile}>
          Close
        </Button>,
        phoneHref ? (
          <Button key='call' href={phoneHref} block={isMobile} type='primary'>
            Call venue
          </Button>
        ) : null,
        <Button
          key='copy'
          type={phoneHref ? 'default' : 'primary'}
          loading={copying}
          disabled={loading || !!error || !usersData?.users?.length}
          onClick={onCopyEmails}
          block={isMobile}
        >
          Copy emails
        </Button>,
      ].filter(Boolean)}
    >
      {loading ? (
        <LoadingWrap>
          <Spin />
        </LoadingWrap>
      ) : null}

      {!loading && error ? (
        <Alert type='error' showIcon message={error} />
      ) : null}

      {!loading && !error ? (
        <ModalBody>
          <ContactLine>
            <ContactLabel>Venue phone</ContactLabel>
            {phoneHref ? (
              <SpartacusPhoneCell phone={venuePhone} />
            ) : (
              <MutedContact>{phoneLabel}</MutedContact>
            )}
          </ContactLine>

          {usersData ? (
            <>
              {showLegacyMessage ? (
                <LegacyAlert
                  type='info'
                  showIcon
                  message='Last seen tracking'
                  description={getSpartacusLastSeenTooltip()}
                />
              ) : null}

              <UsersTable
                size='small'
                pagination={false}
                rowKey='key'
                dataSource={usersData.users}
                columns={USER_COLUMNS}
                locale={{
                  emptyText: 'No users found for this venue',
                }}
              />
            </>
          ) : null}
        </ModalBody>
      ) : null}
    </StyledModal>
  )
}

const StyledModal = styled(Modal)`
  .ant-modal-footer {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-end;
  }

  .ant-modal-footer .ant-btn + .ant-btn {
    margin-left: 0;
  }

  @media (max-width: 767px) {
    .ant-modal {
      margin: 8px auto;
      max-width: calc(100vw - 16px);
      padding-bottom: 0;
      top: 8px;
    }

    .ant-modal-body {
      max-height: calc(100vh - 220px);
      overflow-x: auto;
      overflow-y: auto;
    }

    .ant-modal-footer {
      flex-direction: column;
      padding: 12px 16px 16px;
    }
  }
`

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`

const ContactLine = styled.div`
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const ContactLabel = styled.span`
  color: ${({ theme }) => theme.colours.darkGrey};
  font-size: 13px;
  font-weight: 600;
`

const MutedContact = styled.span`
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 13px;
`

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.lg};
`

const LegacyAlert = styled(Alert)`
  margin-bottom: 0;
`

const UsersTable = styled(Table)`
  .ant-table {
    font-size: 13px;
  }
`

const LastSeenValue = styled.span`
  color: ${({ $untracked, theme }) =>
    $untracked ? theme.colours.oliveGrey : theme.colours.bbqBlack};
  font-style: ${({ $untracked }) => ($untracked ? 'italic' : 'normal')};
`

export default SpartacusVenueUsersModal
